import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import { useWidget } from "/imports/state/WidgetContext";
import { EmptyState } from "/imports/ui/components/shared";
import ScheduleGrid from "/imports/ui/components/scheduleGrid";
import { NO_CAPTURE } from "/imports/libs/analytics";

import useFullSchedule from "./useFullSchedule";
import { weekDayColumns } from "/imports/ui/components/scheduleGrid/gridHelpers";
import { groupByEntity, groupByDate, dayColumns } from "./fullScheduleHelpers";
import FilterBar from "./components/FilterBar";
import FullScheduleBlock from "./components/FullScheduleBlock";
import BookingSheet from "./components/BookingSheet";

// Senior trainers' club-wide calendar: the partners schedule modes rendered
// with the widget's own mobile grid. Read-only apart from attendance + client
// notes (both in the BookingSheet). The API 403s non-seniors; the redirect here is
// just the polite version of that gate.
function TrainerFullScheduleView() {
  const { slug } = useWidget();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const s = useFullSchedule();
  const [sheetBookingId, setSheetBookingId] = useState(null);

  // The sheet reads the live (override-applied) booking so a saved note or
  // attendance mark is reflected immediately, not just after a refetch.
  const sheetBooking = sheetBookingId ? s.bookings.find((b) => b._id === sheetBookingId) || null : null;

  // The API answers 403 with these messages for non-seniors / non-trainers;
  // useFetch only surfaces the message string, so match on it.
  const notAllowed = Boolean(s.error) && /senior trainers|not a trainer/i.test(s.error);
  useEffect(() => {
    if (notAllowed) navigate(`/${slug}/trainer`, { replace: true });
  }, [notAllowed, navigate, slug]);
  if (notAllowed) return null;

  const isWeek = s.filters.view === "week";
  let columns = [];
  let itemsByColumn = {};
  if (s.data) {
    if (isWeek) {
      const grouped = groupByDate({ bookings: s.bookings, weekStart: s.data.weekStart });
      columns = weekDayColumns(grouped.dates, s.data.todayYmd);
      itemsByColumn = grouped.byColumn;
    } else {
      // A picked trainer/court narrows the day to its own column, as in partners; the full roster still feeds the dropdown.
      const dayRoster = s.filters.entityId === "all" ? s.roster : s.roster.filter((r) => r._id === s.filters.entityId);
      columns = dayColumns(dayRoster);
      itemsByColumn = groupByEntity({ bookings: s.bookings, entityType: s.filters.entityType, roster: dayRoster });
    }
  }

  // Week: only today's column. Day: every column, but only when the day shown is today.
  const nowColumnKeys = !s.data ? [] : isWeek ? [s.data.todayYmd] : s.data.date === s.data.todayYmd ? columns.map((c) => c.key) : [];

  // The block's context line shows the "other" axis: courts when looking at
  // trainers, trainers when looking at courts.
  const contextFor = (b) => (s.filters.entityType === "facility" ? (b.trainerNames || [])[0] || "" : b.facilityName || "");

  return (
    <Box className={NO_CAPTURE}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
          {isMobile ? (
            <IconButton size="small" aria-label="My schedule" onClick={() => navigate(`/${slug}/trainer`)} sx={{ flexShrink: 0, ml: -0.5 }}>
              <ArrowBackIcon />
            </IconButton>
          ) : (
            <Button size="small" color="inherit" startIcon={<ArrowBackIcon />} onClick={() => navigate(`/${slug}/trainer`)} sx={{ flexShrink: 0 }}>
              My schedule
            </Button>
          )}
          <Typography variant="h6" fontWeight={600} noWrap>
            Club schedule
          </Typography>
        </Box>
        {isMobile ? (
          <IconButton size="small" color="primary" aria-label="Refresh" onClick={s.refresh} disabled={s.loading} sx={{ flexShrink: 0 }}>
            {s.loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
          </IconButton>
        ) : (
          <Button
            size="small"
            startIcon={s.loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
            onClick={s.refresh}
            disabled={s.loading}
            sx={{ flexShrink: 0 }}
          >
            Refresh
          </Button>
        )}
      </Box>

      <FilterBar
        filters={{ ...s.filters, weekStart: s.data?.weekStart, weekEnd: s.data?.weekEnd, date: s.filters.date || s.data?.date }}
        locations={s.data?.locations || []}
        roster={s.roster}
        loading={s.loading}
        onLocation={s.setLocation}
        onEntityType={s.setEntityType}
        onEntityId={s.setEntityId}
        onView={s.setView}
        onDate={s.setDate}
        onShiftDate={s.shiftDate}
        onToday={s.goToday}
      />

      {s.loading && !s.refreshing ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : s.error ? (
        <EmptyState title="Couldn't load the schedule" description={s.error || "Please try again in a moment."} />
      ) : columns.length === 0 ? (
        <EmptyState
          title={s.filters.entityType === "facility" ? "No courts here" : "No trainers here"}
          description="This location has nothing to show for this view."
        />
      ) : (
        <ScheduleGrid
          columns={columns}
          itemsByColumn={itemsByColumn}
          startHour={s.data?.startHour}
          endHour={s.data?.endHour}
          BlockComponent={FullScheduleBlock}
          blockProps={{ onOpen: (b) => setSheetBookingId(b._id), contextFor }}
          autoScrollToKey={isWeek ? s.data?.todayYmd : undefined}
          nowColumnKeys={nowColumnKeys}
          timeZone={s.data?.timeZone}
        />
      )}

      <BookingSheet
        booking={sheetBooking}
        onClose={() => setSheetBookingId(null)}
        onAttendanceChanged={s.applyAttendance}
        onClientNoteSaved={s.applyClientNote}
      />
    </Box>
  );
}

export default TrainerFullScheduleView;
